import React from 'react';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';

interface CommitBarProps {
  commitLabel: string;
  onCommit: () => void;
  onSaveAsTemplate: () => void;
  onCancel: () => void;
  adjustEndDates: boolean;
  onAdjustEndDatesChange: (value: boolean) => void;
  isCommitting?: boolean;
  showAdjustEndDates?: boolean;
  /** False in template-only contexts (mobile has no template context; web's `/templates/generate` does) — only Save as template + Cancel apply. */
  showCommit?: boolean;
}

/**
 * Sprint 31, story-161. Footer: cancel / save-as-template / adjust-end-dates / primary commit.
 * Not sticky (walkthrough fix, sprint 38): PlanReview is a fixed-height column with this bar as the
 * last row, so sticking it to the viewport only slid it over the bottom of the card list.
 */
export const CommitBar: React.FC<CommitBarProps> = ({
  commitLabel,
  onCommit,
  onSaveAsTemplate,
  onCancel,
  adjustEndDates,
  onAdjustEndDatesChange,
  isCommitting,
  showAdjustEndDates = true,
  showCommit = true,
}) => {
  const t = useTranslation('aiPlan');
  return (
    <div data-testid="ai-plan-commit-bar" className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 px-4 py-3">
      {showAdjustEndDates ? (
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={adjustEndDates}
            onChange={(e) => onAdjustEndDatesChange(e.target.checked)}
          />
          {t.adjustEndDates}
        </label>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={onCancel} data-testid="ai-plan-cancel" className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
          {t.commitCancel}
        </button>
        <button
          type="button"
          onClick={onSaveAsTemplate}
          data-testid="ai-plan-save-template"
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700">
          {t.commitSaveTemplate}
        </button>
        {showCommit && (
          <button
            type="button"
            onClick={onCommit}
            disabled={isCommitting}
            data-testid="ai-plan-commit"
            className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white disabled:opacity-60 flex items-center gap-2">
            {isCommitting && <Loading size="sm" />}
            {commitLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default CommitBar;
